import { useMemo, useState } from 'react'

import type { AccountLedger } from '../api/generated/model'
import { formatCompactMajor, formatDate, formatDateShort, formatMoney, toMajor } from '../lib/money'

const WIDTH = 900
const HEIGHT = 320
const PAD = { top: 16, right: 16, bottom: 28, left: 64 }

/**
 * The primary view: projected balance over the horizon.
 *
 * Drawn by hand rather than with a charting library. The shapes needed here are specific —
 * a zero line that means something, a buffer band, shading only where the balance is
 * actually negative — and colour has to stay meaningful: a breach is the only red on the
 * screen (charter 6.7).
 *
 * Every point is inspectable, because a figure the user cannot trace back to the movements
 * that caused it is not finished (charter principle 2).
 */
export function BalanceTimeline({ ledger }: { ledger: AccountLedger }) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)

  const chart = useMemo(() => {
    const values = ledger.points.map((p) => toMajor(p.closingMinor, ledger.currency))
    const buffer = toMajor(ledger.bufferTargetMinor, ledger.currency)

    // The domain always includes zero and the buffer, so those reference lines are never
    // off-screen — they are the whole point of the chart.
    const candidates = [...values, 0, buffer]
    const rawMin = Math.min(...candidates)
    const rawMax = Math.max(...candidates)
    const span = rawMax - rawMin || 1
    const min = rawMin - span * 0.08
    const max = rawMax + span * 0.08

    const plotW = WIDTH - PAD.left - PAD.right
    const plotH = HEIGHT - PAD.top - PAD.bottom
    const x = (i: number) =>
      PAD.left + (values.length <= 1 ? 0 : (i / (values.length - 1)) * plotW)
    const y = (v: number) => PAD.top + plotH - ((v - min) / (max - min)) * plotH

    const line = values.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(v)}`).join(' ')
    // Closed against the zero line, not the chart floor. That is what makes the negative
    // clip correct: intersecting "below zero" with this shape yields only the stretches
    // where the balance is genuinely negative, rather than a band under the whole curve.
    const area = `${line} L${x(values.length - 1)},${y(0)} L${x(0)},${y(0)} Z`

    return { values, buffer, x, y, line, area, min, max, plotH }
  }, [ledger])

  const minIndex = ledger.points.findIndex((p) => p.date === ledger.minimumBalanceDate)
  const active = hoverIndex ?? minIndex
  const activePoint = ledger.points[active]
  const activeMovements = activePoint
    ? ledger.movements.filter((m) => m.date === activePoint.date)
    : []

  const ticks = useMemo(() => {
    const count = 5
    return Array.from({ length: count }, (_, i) => {
      const v = chart.min + ((chart.max - chart.min) * i) / (count - 1)
      return { v, y: chart.y(v) }
    })
  }, [chart])

  const dateTicks = useMemo(() => {
    const count = Math.min(6, ledger.points.length)
    return Array.from({ length: count }, (_, i) => {
      const idx = Math.round((i / (count - 1)) * (ledger.points.length - 1))
      return { idx, date: ledger.points[idx].date }
    })
  }, [ledger.points])

  function handleMove(event: React.MouseEvent<SVGSVGElement>) {
    const rect = event.currentTarget.getBoundingClientRect()
    const ratio = (event.clientX - rect.left) / rect.width
    const svgX = ratio * WIDTH
    const plotW = WIDTH - PAD.left - PAD.right
    const idx = Math.round(((svgX - PAD.left) / plotW) * (ledger.points.length - 1))
    setHoverIndex(Math.max(0, Math.min(ledger.points.length - 1, idx)))
  }

  return (
    <div className="timeline">
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="timeline-svg"
        role="img"
        aria-label={`Projected balance for ${ledger.name}`}
        onMouseMove={handleMove}
        onMouseLeave={() => setHoverIndex(null)}
      >
        <defs>
          <clipPath id={`under-${ledger.accountId}`}>
            <path d={chart.area} />
          </clipPath>
        </defs>

        {ticks.map((t) => (
          <g key={t.v}>
            <line x1={PAD.left} x2={WIDTH - PAD.right} y1={t.y} y2={t.y} className="grid" />
            <text x={PAD.left - 10} y={t.y + 4} className="axis-label" textAnchor="end">
              {formatCompactMajor(t.v, ledger.currency)}
            </text>
          </g>
        ))}

        <path d={chart.area} className="area" />

        {/* Red appears only where the balance is genuinely below zero. */}
        <rect
          x={PAD.left}
          y={chart.y(0)}
          width={WIDTH - PAD.left - PAD.right}
          height={Math.max(0, HEIGHT - PAD.bottom - chart.y(0))}
          className="area-negative"
          clipPath={`url(#under-${ledger.accountId})`}
        />

        {chart.buffer > 0 && (
          <line
            x1={PAD.left}
            x2={WIDTH - PAD.right}
            y1={chart.y(chart.buffer)}
            y2={chart.y(chart.buffer)}
            className="buffer-line"
          />
        )}
        <line
          x1={PAD.left}
          x2={WIDTH - PAD.right}
          y1={chart.y(0)}
          y2={chart.y(0)}
          className="zero-line"
        />

        <path d={chart.line} className="balance-line" />

        {minIndex >= 0 && (
          <circle
            cx={chart.x(minIndex)}
            cy={chart.y(chart.values[minIndex])}
            r={4}
            className={`min-marker ${ledger.minimumBalanceMinor < 0 ? 'is-negative' : ''}`}
          />
        )}

        {hoverIndex !== null && (
          <line
            x1={chart.x(hoverIndex)}
            x2={chart.x(hoverIndex)}
            y1={PAD.top}
            y2={HEIGHT - PAD.bottom}
            className="hover-guide"
          />
        )}

        {dateTicks.map((t) => (
          <text
            key={t.date}
            x={chart.x(t.idx)}
            y={HEIGHT - 8}
            className="axis-label"
            textAnchor="middle"
          >
            {formatDateShort(t.date)}
          </text>
        ))}
      </svg>

      <div className="timeline-readout">
        <div className="readout-head">
          <span className="readout-date">
            {activePoint ? formatDate(activePoint.date) : '—'}
            {hoverIndex === null && <span className="readout-tag">lowest point</span>}
          </span>
          <span
            className={`readout-balance ${
              activePoint && activePoint.closingMinor < 0 ? 'is-negative' : ''
            }`}
          >
            {activePoint ? formatMoney(activePoint.closingMinor, ledger.currency) : '—'}
          </span>
        </div>

        {activeMovements.length > 0 ? (
          <ul className="movements">
            {activeMovements.map((m, i) => (
              <li key={`${m.sourceId}-${i}`}>
                <span className="movement-source">{m.sourceName}</span>
                {m.state !== 'PROJECTED' && (
                  <span className={`state-tag state-${m.state.toLowerCase()}`}>
                    {m.state.toLowerCase()}
                  </span>
                )}
                <span className={`movement-amount ${m.direction === 'OUT' ? 'is-out' : 'is-in'}`}>
                  {m.direction === 'OUT' ? '−' : '+'}
                  {formatMoney(m.amountMinor, ledger.currency)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="movements-empty">No movements on this day.</p>
        )}
      </div>
    </div>
  )
}
